import {
  HelpPathOutlineIcon,
  MoonSolidIcon,
  SunSolidIcon,
} from '@ikyucom/design-system-icons-react'
import * as Popover from '@radix-ui/react-popover'
import type { PropsWithChildren, ReactNode } from 'react'
import { IkyuDayBalloon } from '~/components/IkyuDayBalloon'
import Image from '~/components/image/Image'
import { PAYPAY_IMAGE_URL } from '~/lib/constants/constants'
import { css, cva, sva } from '~/styled-system/css'
import type { RecipeVariantProps } from '~/styled-system/css'

const dinnerPoints = import.meta.env.VITE_MODE === 'ppg' ? 50 : 200
const lunchPoints = import.meta.env.VITE_MODE === 'ppg' ? 10 : 100

const styles = sva({
  slots: [
    'root',
    'text',
    'span',
    'point',
    'pointValue',
    'help',
    'list',
    'term',
    'definition',
    'note',
  ],
  base: {
    root: {
      display: 'flex',
      alignItems: 'center',
    },
    text: {
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      lineHeight: '[21px]',
    },
    span: {
      display: 'flex',
      alignItems: 'center',
    },
    point: {
      fontSize: 'sm',
      color: 'accent.pink',
      display: 'flex',
      alignItems: 'center',
    },
    pointValue: {
      fontSize: 'xl',
    },
    help: {
      color: 'gray.700',
    },
    list: {
      display: 'grid',
      gridTemplateRows: '2',
      gridTemplateColumns: '[max-content 1fr]',
      py: '3',
    },
    term: {
      display: 'flex',
      alignItems: 'center',
    },
    definition: {
      display: 'flex',
      alignItems: 'center',
      columnGap: '1',
      _before: {
        content: '"："',
      },
    },
    note: {
      fontSize: 'sm',
      color: 'gray.700',
    },
  },
  variants: {
    type: {
      long: {
        root: {
          columnGap: '4',
        },
        text: {
          fontSize: 'xs',
          position: 'relative',
          columnGap: '1',
          _before: {
            content: '""',
            position: 'absolute',
            top: '[calc(50% - 0.5rem)]',
            left: '[-8px]',
            h: '[1rem]',
            w: '[1px]',
            bg: 'gray.300',
          },
        },
      },
      short: {
        root: {
          columnGap: '1',
        },
        text: {
          fontSize: 'sm',
        },
      },
    },
  },
})

type PointProgramTextProps = { showIkyuDayBalloon?: boolean } & RecipeVariantProps<typeof styles>

export function PointProgramText({ type, showIkyuDayBalloon }: PointProgramTextProps) {
  const { root, text, span, point, pointValue, help, list, term, definition, note } = styles({
    type,
  })
  if (type === 'long') {
    return (
      <div className={root}>
        <PayPayIcon />
        <p className={text}>
          貯まる・使える
          <span className={span}>
            <MealPeriodIcon period="dinner" /> 人数×
            <span className={point}>
              <span className={pointValue}>{dinnerPoints}</span>円分
            </span>
            <span className={css({ px: '[2px]' })}>／</span>
            <MealPeriodIcon period="lunch" /> 人数×
            <span className={point}>
              <span className={pointValue}>{lunchPoints}</span>円分
            </span>
          </span>
          もらえる
        </p>
      </div>
    )
  } else {
    return (
      <div>
        {showIkyuDayBalloon && <IkyuDayBalloon />}
        <div className={root}>
          <PayPayIcon />
          <p className={text}>
            人数×
            <span className={point}>
              最大<span className={pointValue}>{dinnerPoints}</span>円分
            </span>
            もらえる
          </p>
          <HelpPopover
            content={
              <>
                <p>ご予約人数に応じて、以下のポイントが付与されます。</p>
                <dl className={list}>
                  <dt className={term}>
                    <MealPeriodIcon period="dinner" />
                    ディナー
                  </dt>
                  <dd className={definition}>
                    <PayPayIcon size={13} />
                    人数×{dinnerPoints}円分
                  </dd>
                  <dt className={term}>
                    <MealPeriodIcon period="lunch" />
                    ディナー以外の時間帯
                  </dt>
                  <dd className={definition}>
                    <PayPayIcon size={13} />
                    人数×{lunchPoints}円分
                  </dd>
                </dl>
                <ul className={note}>
                  <li>※大人の人数のみ対象です</li>
                </ul>
              </>
            }
          >
            <HelpPathOutlineIcon width={16} height={16} className={help} />
          </HelpPopover>
        </div>
      </div>
    )
  }
}

function PayPayIcon({ size = 16 }: { size?: number }) {
  return <Image src={PAYPAY_IMAGE_URL} width={size} height={size} alt="PayPay" />
}

const mealPeriodIconStyle = cva({
  base: {
    display: 'block',
    rounded: 'sm',
    p: '[2px]',
    mr: '1',
    fontSize: 'xs',
    color: 'white',
  },
  variants: {
    period: {
      dinner: {
        bgColor: 'dinner.label',
      },
      lunch: {
        bgColor: 'lunch.label',
      },
    },
  },
})

function MealPeriodIcon(props: NonNullable<RecipeVariantProps<typeof mealPeriodIconStyle>>) {
  const className = mealPeriodIconStyle(props)
  const Icon = props.period === 'dinner' ? MoonSolidIcon : SunSolidIcon
  return (
    <span className={className}>
      <Icon />
    </span>
  )
}

function HelpPopover(props: PropsWithChildren<{ content: ReactNode }>) {
  return (
    <Popover.Root>
      <Popover.Trigger>{props.children}</Popover.Trigger>
      <Popover.Content
        align="end"
        alignOffset={-18}
        className={css({
          rounded: 'default',
          shadow: 'outline.lg',
          p: '4',
          mx: '2',
          bgColor: 'white',
          fontSize: 'base',
          zIndex: '1',
        })}
      >
        {props.content}
        <Popover.Arrow width={12} height={6} className={css({ fill: 'white' })} />
      </Popover.Content>
    </Popover.Root>
  )
}
